/**
 * @description Settings for controller
 * @module API controller / Session
 */
module.exports = {
  login: {
    /**
     * @description Get request settings (Login)
     * @param  {object} options
     * @param  {string} options.email - The account ID.
     * @param  {string} options.password - The profile ID.
     * @param  {number} options.inviteId - The invitation ID (optional).
     * @return {object} Request Settings
     */
    settings({ email, password, inviteId, dpopProof }) {
      const body = {
        email,
        password,
      }

      if (inviteId) {
        body.invite_id = inviteId
      }

      return {
        endpoint: '/sessions/login',
        body,
        method: 'POST',
        // Forward the DPoP proof generated in fetchLogin so the BE can bind the
        // key fingerprint to this user at login time (SIM-255).
        ...(dpopProof && { headers: { DPoP: dpopProof } }),
      }
    },
  },

  loginLinkRelationshipsProfile: {
    /**
     * @description Get request settings (Login)
     * @param  {object} options
     * @param  {string} options.email - The account ID.
     * @param  {string} options.password - The profile ID.
     * @return {object} Request Settings
     */
    settings({ email, password, token, dpopProof }) {
      return {
        endpoint: '/sessions/login_link_personal',
        body: {
          email,
          password,
          token,
        },
        method: 'POST',
        ...(dpopProof && { headers: { DPoP: dpopProof } }),
      }
    },
  },

  ssoLogin: {
    /**
     * @description Get request settings (SSO Login)
     * @param  {object} options
     * @param  {string} options.token    - The SSO session token from the SAML/OIDC redirect.
     * @param  {string} options.dpopProof - DPoP proof JWT (RFC 9449); null when WebCrypto unavailable.
     * @return {object} Request Settings
     */
    settings({ token, dpopProof }) {
      return {
        endpoint: '/sessions/sso_login',
        headers: {
          Authorization: `Token sso=${token}`,
          ...(dpopProof && { DPoP: dpopProof }),
        },
        method: 'GET',
      }
    },
  },

  impersonate: {
    /**
     * @description Get request settings (Impersonate)
     * @param  {string} token - The account ID.
     * @return {object} Request Settings
     */
    settings(token) {
      return {
        endpoint: '/sessions/impersonate_login',
        headers: {
          Authorization: `Token impersonate=${token}`,
        },
        method: 'POST',
      }
    },
  },

  current: {
    /**
     * @description Get request settings (Current session, SIM-388)
     * Rebuilds the in-memory user state after a full page reload. The Authorization header
     * (regular, SSO or impersonation token) is added by ApiEndpoint from the persisted token.
     * @return {object} Request Settings
     */
    settings() {
      return {
        endpoint: '/sessions/current',
        method: 'GET',
      }
    },
  },

  logout: {
    clearTimeout: true,
    /**
     * @description Get request settings (Logout)
     * @param { number } accountId
     * @return {object} Request Settings
     */
    settings(accountId) {
      return {
        endpoint: `/account/${accountId}/sessions/logout`,
        method: 'DELETE',
      }
    },
  },
}
